/**
 * Employee Desktop — Mon Contrat
 */
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FileText, Calendar, Clock, Shield, Loader2 } from 'lucide-react';
import config from 'config';

const API = config.API_URL;

const STATUS_MAP = {
  ACTIVE: { label: 'Actif', cls: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
  ON_LEAVE: { label: 'En congé', cls: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
  SUSPENDED: { label: 'Suspendu', cls: 'text-red-400 bg-red-400/10 border-red-400/20' },
  TERMINATED: { label: 'Terminé', cls: 'text-white/40 bg-white/5 border-white/10' },
};

export default function EmployeeContractPage() {
  const { token } = useOutletContext();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/employees/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setEmployee(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>;

  if (!employee) return <div className="p-8 text-white/40">Aucune information disponible.</div>;

  const st = STATUS_MAP[employee.status] || STATUS_MAP.ACTIVE;

  const fields = [
    { icon: FileText, color: 'text-[#C9A24D] bg-[#C9A24D]/10', label: 'Type de contrat', value: employee.contract_type, testId: 'contract-type' },
    { icon: Clock, color: 'text-blue-400 bg-blue-400/10', label: 'Heures / semaine', value: `${employee.weekly_hours}h`, testId: 'weekly-hours' },
    { icon: Calendar, color: 'text-emerald-400 bg-emerald-400/10', label: 'Date de début', value: new Date(employee.start_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }), testId: 'start-date' },
    { icon: Shield, color: 'text-purple-400 bg-purple-400/10', label: 'Statut', value: null, testId: 'status' },
  ];

  return (
    <div className="p-8 max-w-4xl" data-testid="desktop-contract-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white">Mon contrat</h1>
        <p className="text-white/40 text-sm mt-1">Informations relatives à votre contrat de travail</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        {fields.map(f => (
          <div key={f.testId} className="p-5 rounded-2xl bg-white/[0.03] border border-white/5" data-testid={`desktop-${f.testId}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${f.color}`}>
                <f.icon className="w-5 h-5" />
              </div>
              <span className="text-white/40 text-sm">{f.label}</span>
            </div>
            {f.value ? (
              <p className="text-white text-xl font-semibold">{f.value}</p>
            ) : (
              <span className={`inline-flex px-4 py-1.5 rounded-full text-xs font-semibold border ${st.cls}`}>
                {st.label}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Employee details */}
      <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5" data-testid="desktop-employee-details">
        <h3 className="text-white/60 text-sm font-medium mb-4">Informations salarié</h3>
        <div className="grid grid-cols-2 gap-y-4 text-sm">
          <div>
            <span className="text-white/30 block mb-1">Nom complet</span>
            <span className="text-white font-medium">{employee.driver_name}</span>
          </div>
          <div>
            <span className="text-white/30 block mb-1">ID salarié</span>
            <span className="text-white/60 font-mono text-xs">{employee.id}</span>
          </div>
          <div>
            <span className="text-white/30 block mb-1">Créé le</span>
            <span className="text-white/60">{employee.created_at ? new Date(employee.created_at).toLocaleDateString('fr-FR') : '-'}</span>
          </div>
          <div>
            <span className="text-white/30 block mb-1">Mis à jour le</span>
            <span className="text-white/60">{employee.updated_at ? new Date(employee.updated_at).toLocaleDateString('fr-FR') : '-'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
